/**
 * Service Principal API Client
 *
 * Feature: 023-service-principal-registry
 * Task: T014 - Create servicePrincipalApi client
 * Task: T024 - Add create/update API methods
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Frontend HTTP client for service principal registry endpoints
 * Uses centralized apiClient for MSAL token injection
 */

import { apiClient } from '../../../services/apiClient';

import {
  ServicePrincipal,
  SpWorkspaceAssignment,
  SpReconciliationLog,
  CreateServicePrincipalRequest,
  UpdateServicePrincipalRequest,
  CreateSpWorkspaceAssignmentRequest,
  ServicePrincipalListResponse,
  SpWorkspaceAssignmentListResponse,
  SpDashboardSummary,
  ServicePrincipalFilters,
  AssignmentFilters,
  ReconciliationLogFilters,
  DriftFilters,
  DriftStatus,
} from '../types';

const API_BASE = '/api/v1/service-principals';

/**
 * Generic API wrapper using centralized apiClient with MSAL auth
 */
async function apiFetch<T>(endpoint: string, options?: { method?: string; body?: string }): Promise<T> {
  const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE}${endpoint}`;

  const config: { method?: string; data?: unknown } = {};
  if (options?.method) {
    config.method = options.method;
  }
  if (options?.body) {
    config.data = JSON.parse(options.body);
  }

  const response = await apiClient.request<T>({
    url,
    method: (config.method as 'GET' | 'POST' | 'PUT' | 'DELETE') || 'GET',
    data: config.data,
  });

  return response.data;
}

/**
 * Build query string from filter params
 */
function buildQueryString(params: Record<string, unknown>): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const qs = queryParams.toString();
  return qs ? `?${qs}` : '';
}

// =============================================================================
// Service Principal CRUD API (US1, US2)
// =============================================================================

/**
 * Get all service principals with optional filtering
 * Task: T011, T014
 */
export async function getServicePrincipals(
  filters?: ServicePrincipalFilters
): Promise<ServicePrincipalListResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  return apiFetch<ServicePrincipalListResponse>(`${qs}`);
}

/**
 * Get service principal by ID with assignments
 * Task: T011, T017
 */
export async function getServicePrincipalById(id: string): Promise<ServicePrincipal> {
  return apiFetch<ServicePrincipal>(`/${id}`);
}

/**
 * Get service principal with assignments summary
 */
export async function getServicePrincipalWithSummary(id: string): Promise<
  ServicePrincipal & {
    assignmentCount: number;
    activeDriftCount: number;
    assignments: SpWorkspaceAssignment[];
  }
> {
  return apiFetch<
    ServicePrincipal & {
      assignmentCount: number;
      activeDriftCount: number;
      assignments: SpWorkspaceAssignment[];
    }
  >(`/${id}?include=assignments`);
}

/**
 * Create new service principal
 * Task: T020, T024
 */
export async function createServicePrincipal(
  data: CreateServicePrincipalRequest
): Promise<ServicePrincipal> {
  return apiFetch<ServicePrincipal>('', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update existing service principal
 * Task: T021, T024
 */
export async function updateServicePrincipal(
  id: string,
  data: UpdateServicePrincipalRequest
): Promise<ServicePrincipal> {
  return apiFetch<ServicePrincipal>(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete service principal (soft delete - sets status to DELETED)
 * Task: T022
 */
export async function deleteServicePrincipal(id: string): Promise<void> {
  await apiFetch<void>(`/${id}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Workspace Assignment API (US1, US2)
// =============================================================================

/**
 * Get assignments for a service principal
 */
export async function getAssignments(
  servicePrincipalId: string,
  filters?: AssignmentFilters
): Promise<SpWorkspaceAssignmentListResponse> {
  const qs = filters ? buildQueryString(filters) : '';
  return apiFetch<SpWorkspaceAssignmentListResponse>(`/${servicePrincipalId}/assignments${qs}`);
}

/**
 * Create new workspace assignment
 */
export async function createAssignment(
  servicePrincipalId: string,
  data: CreateSpWorkspaceAssignmentRequest
): Promise<SpWorkspaceAssignment> {
  return apiFetch<SpWorkspaceAssignment>(`/${servicePrincipalId}/assignments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Delete workspace assignment
 */
export async function deleteAssignment(
  servicePrincipalId: string,
  assignmentId: string
): Promise<void> {
  await apiFetch<void>(`/${servicePrincipalId}/assignments/${assignmentId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Dashboard API (US1)
// =============================================================================

/**
 * Get dashboard summary
 * Task: T011
 */
export async function getDashboardSummary(): Promise<SpDashboardSummary> {
  return apiFetch<SpDashboardSummary>('/dashboard');
}

// =============================================================================
// Reconciliation API (US3)
// =============================================================================

// Spec 298 FR-006 (evidence/zero-fallbacks.md rows 46-50): apiFetch returns each route's body as it is. The four
// reads below were typed with fields no body carries (isRunning/lastRun/nextScheduledRun, items/total, exports),
// so the Reconciliation page read 0 and empty lists. They are typed with the bodies the backend handlers send
// (backend modules/service-principals/controllers/servicePrincipalController.ts).

/** `{ success, data }`, the body every service-principal read sends. */
export interface SpApiBody<T> {
  success: boolean;
  data: T;
}

/** `{ success, data, pagination }`, the logs and drifts reads. `total` counts the filtered list, not a page. */
export interface SpPagedBody<T> extends SpApiBody<T[]> {
  pagination: { page: number; pageSize: number; total: number };
}

/** GET /reconciliation/status: reconciliationService.getReconciliationStatus(). recentRuns is newest first. */
export interface ReconciliationStatusSummary {
  lastDaily: SpReconciliationLog | null;
  lastWeekly: SpReconciliationLog | null;
  recentRuns: SpReconciliationLog[];
}

/** GET /reconciliation/drifts: one active drift with its names. */
export type ActiveDrift = SpWorkspaceAssignment & {
  servicePrincipalName: string;
  workspaceName: string;
};

/** GET /exports: exportService.listExports(), one export file. */
export interface SpExportFile {
  filename: string;
  createdAt: string;
  size: number;
  storageType: 'blob' | 'local';
  url?: string;
}

/**
 * Get reconciliation status
 * Task: T031
 */
export async function getReconciliationStatus(): Promise<SpApiBody<ReconciliationStatusSummary>> {
  return apiFetch<SpApiBody<ReconciliationStatusSummary>>('/reconciliation/status');
}

/**
 * Trigger manual reconciliation run
 * Task: T030
 */
export async function triggerReconciliation(
  type: 'daily_permissions' | 'weekly_entra' | 'weekly_export' = 'daily_permissions'
): Promise<{ runId: string; message: string }> {
  return apiFetch<{ runId: string; message: string }>('/reconcile', {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
}

/**
 * Get reconciliation logs
 * Task: T032
 */
export async function getReconciliationLogs(
  filters?: ReconciliationLogFilters
): Promise<SpPagedBody<SpReconciliationLog>> {
  const qs = filters ? buildQueryString(filters) : '';
  return apiFetch<SpPagedBody<SpReconciliationLog>>(`/reconciliation/logs${qs}`);
}

/**
 * Get reconciliation run details
 */
export async function getReconciliationRun(runId: string): Promise<SpReconciliationLog> {
  return apiFetch<SpReconciliationLog>(`/reconciliation/logs/${runId}`);
}

// =============================================================================
// Drift Management API (US4)
// =============================================================================

/**
 * Get active drifts
 * Task: T039
 */
export async function getActiveDrifts(filters?: DriftFilters): Promise<SpPagedBody<ActiveDrift>> {
  const qs = filters ? buildQueryString(filters) : '';
  return apiFetch<SpPagedBody<ActiveDrift>>(`/reconciliation/drifts${qs}`);
}

/**
 * Fix a drift manually
 * Task: T040
 */
export async function fixDrift(driftId: string): Promise<{
  success: boolean;
  newStatus: DriftStatus;
  message: string;
}> {
  return apiFetch<{
    success: boolean;
    newStatus: DriftStatus;
    message: string;
  }>(`/reconciliation/drifts/${driftId}/fix`, {
    method: 'POST',
  });
}

/**
 * Resolve a drift manually (mark as resolved without fixing)
 * Task: T041
 */
export async function resolveDrift(
  driftId: string,
  resolution: string
): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>(
    `/reconciliation/drifts/${driftId}/resolve`,
    {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }
  );
}

// =============================================================================
// Export API (US6)
// =============================================================================

/**
 * Get export history
 * Task: T056
 */
export async function getExportHistory(): Promise<SpApiBody<SpExportFile[]>> {
  return apiFetch<SpApiBody<SpExportFile[]>>('/exports');
}

/**
 * Trigger manual export
 */
export async function triggerExport(): Promise<{ filename: string; message: string }> {
  return apiFetch<{ filename: string; message: string }>('/export', {
    method: 'POST',
  });
}

// =============================================================================
// Export API instance (class-based for consistency with other modules)
// =============================================================================

class ServicePrincipalApi {
  // Service Principals
  getAll = getServicePrincipals;
  getById = getServicePrincipalById;
  getWithSummary = getServicePrincipalWithSummary;
  create = createServicePrincipal;
  update = updateServicePrincipal;
  delete = deleteServicePrincipal;

  // Assignments
  getAssignments = getAssignments;
  createAssignment = createAssignment;
  deleteAssignment = deleteAssignment;

  // Dashboard
  getDashboardSummary = getDashboardSummary;

  // Reconciliation
  getReconciliationStatus = getReconciliationStatus;
  triggerReconciliation = triggerReconciliation;
  getReconciliationLogs = getReconciliationLogs;
  getReconciliationRun = getReconciliationRun;

  // Drifts
  getActiveDrifts = getActiveDrifts;
  fixDrift = fixDrift;
  resolveDrift = resolveDrift;

  // Exports
  getExportHistory = getExportHistory;
  triggerExport = triggerExport;
}

export const servicePrincipalApi = new ServicePrincipalApi();
